import React from 'react';
import {
  Alert, Box, Button, Chip, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem,
  Paper, Stack, TextField, Typography,
} from '@mui/material';
import { Tooltip, useMediaQuery, useTheme } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SendIcon from '@mui/icons-material/Send';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import HistoryIcon from '@mui/icons-material/History';
import MoreIcon from '@mui/icons-material/MoreVert';
import PublishIcon from '@mui/icons-material/Publish';
import CheckIcon from '@mui/icons-material/Check';
import { relativeTime } from '../../../utils/relativeTime';
import CircularProgress from '@mui/material/CircularProgress';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import FlowPhonePreview from '../../flows/FlowPhonePreview';
import HubIcon from '@mui/icons-material/HubOutlined';
import FlowTypeControl from '../FlowTypeControl';
import ValidationStatus from '../ValidationStatus';
import GenerationProgress from './GenerationProgress';
import SaveStatusControl from '../SaveStatusControl';

const EXAMPLES = [
  'A booking form for my dance studio',
  'An enquiry form for a dental clinic',
  'A feedback survey after an appointment',
];

/**
 * The AI half of the builder: describe a flow, watch it appear, say what to change.
 *
 * Deliberately has no canvas, no palette and no property panel. Someone who knows what a
 * ChipsSelector is does not need this mode, and someone who does not is the person it exists
 * for, putting the builder's furniture around the box would only invite them to fiddle with
 * something they came here precisely to avoid.
 *
 * It owns no flow of its own. `flowJson` comes in and every result goes back out through
 * `onFlowChange`, so the manual builder and this share one flow rather than two copies that
 * drift. That is also what lets `onRefine` send the CURRENT flow to the server: edits made by
 * hand between two chat messages survive, instead of being replayed away.
 */
const AiFlowBuilder = ({
  flowJson,
  sessionId,
  messages,
  busy,
  startedAt,
  progressNote,
  error,
  onGenerate,
  onRefine,
  assets = [],
  uploading = false,
  onUpload,
  onRemoveAsset,
  assetUrl,
  flow,
  dirty,
  autoSaveError,
  syncStatus,
  autoSaveEnabled,
  onToggleAutoSave,
  onSaveNow,
  saveDisabledReason,
  onPublish,
  publishDisabledReason,
  onSendToPhone,
  sendToPhoneDisabledReason,
  onOpenPreview,
  previewDisabledReason,
  onOpenTriggers,
  triggersDisabledReason,
  validationMap,
  validationScreens = [],
  validationMeta,
  onOpenValidation,
  onFixProblems,
  flowType = 'STATIC',
  onFlowTypeChange,
  sessions = [],
  currentSessionId,
  viewing,
  onOpenSession,
  onCloseSession,
  onContinueSession,
  onStartAfresh,
}) => {
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [moreAnchorEl, setMoreAnchorEl] = React.useState(null);
  const closeMoreMenu = () => setMoreAnchorEl(null);
  const earlier = sessions.filter((s) => s.sessionId !== currentSessionId);
  const [input, setInput] = React.useState('');
  const [screenIndex, setScreenIndex] = React.useState(0);
  const [mobileTab, setMobileTab] = React.useState('chat');
  const [saving, setSaving] = React.useState(false);
  const scroller = React.useRef(null);
  const filePicker = React.useRef(null);
  const theme = useTheme();
  // A phone has room for the conversation or the preview, not both. They become tabs rather
  // than the preview being dropped: hiding it left the one thing worth looking at, whether it
  // built the right form, visible only on a desktop.
  //
  // Touch counts as narrow whatever width it reports: a phone asking for the desktop site says
  // ~980px, and side-by-side panes at that width on a 6" screen are unreadable.
  const isTouch = useMediaQuery('(hover: none) and (pointer: coarse)');
  const narrow = useMediaQuery(theme.breakpoints.down('md')) || isTouch;

  const screens = flowJson?.screens || [];
  const hasFlow = screens.length > 0;

  React.useEffect(() => {
    // A regenerated flow may have fewer screens than the one being shown.
    setScreenIndex((current) => Math.min(current, Math.max(screens.length - 1, 0)));
  }, [screens.length]);

  const flowKey = React.useMemo(() => JSON.stringify(flowJson || null), [flowJson]);
  const firstFlow = React.useRef(true);
  React.useEffect(() => {
    // On a phone, show the result the moment there is one, the point of asking was to see it.
    // Only on the FIRST flow, though: yanking the view away mid-conversation on every later
    // turn would take the composer out from under someone's thumb.
    if (!hasFlow) return;
    if (firstFlow.current) {
      firstFlow.current = false;
      setMobileTab('preview');
    }
  }, [flowKey, hasFlow]);

  React.useEffect(() => {
    const box = scroller.current;
    if (!box) return;
    // Not every environment implements smooth scrollTo; falling back keeps the newest message
    // visible rather than throwing on the way to it.
    if (typeof box.scrollTo === 'function') {
      box.scrollTo({ top: box.scrollHeight, behavior: 'smooth' });
    } else {
      box.scrollTop = box.scrollHeight;
    }
  }, [messages, busy]);

  const submit = (text) => {
    const said = (text ?? input).trim();
    if (!said || busy) return;
    setInput('');
    if (sessionId) {
      onRefine(said);
    } else {
      onGenerate(said);
    }
  };

  const onKeyDown = (event) => {
    // Enter sends, Shift+Enter breaks the line, the convention of every chat box people
    // already use, so nobody has to be told. NOT on a phone: there is no Shift to hold, so
    // the return key has to stay a return key and the send button does the sending.
    if (event.key === 'Enter' && !event.shiftKey && !narrow) {
      event.preventDefault();
      submit();
    }
  };

  const pickFiles = (event) => {
    const chosen = Array.from(event.target.files || []);
    // Reset first: choosing the same file twice in a row fires no change event otherwise.
    event.target.value = '';
    if (chosen.length > 0 && onUpload) onUpload(chosen);
  };

  const composer = (autoFocus) => (
    <Stack direction="row" spacing={1} alignItems="flex-end">
      <input
        ref={filePicker}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={pickFiles}
      />
      <Tooltip title="Add your own images">
        <span>
          <IconButton
            onClick={() => filePicker.current?.click()}
            disabled={busy || uploading}
            aria-label="Add images"
            sx={{ mb: 0.5, width: 44, height: 44 }}
          >
            {uploading ? <CircularProgress size={20} /> : <AddPhotoAlternateIcon />}
          </IconButton>
        </span>
      </Tooltip>
      <TextField
        fullWidth
        multiline
        maxRows={4}
        autoFocus={autoFocus}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={busy}
        placeholder={hasFlow ? 'What should change?' : 'Describe the form you need…'}
        // Unmasked in session replay: what someone types here is the whole point of the
        // conversation, and a masked "•••" box tells us nothing about why generation went well
        // or badly for them.
        inputProps={{ 'aria-label': hasFlow ? 'Describe a change' : 'Describe your flow', 'data-ph-unmask': true }}
      />
      <IconButton
        color="primary"
        onClick={() => submit()}
        disabled={busy || !input.trim()}
        aria-label="Send"
        // 44px is the floor for a thumb; the default icon button is smaller than that.
        sx={{ mb: 0.5, width: 44, height: 44 }}
      >
        <SendIcon />
      </IconButton>
    </Stack>
  );

  if (!hasFlow && messages.length === 0) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Box sx={{ width: '100%', maxWidth: 560 }}>
          <Stack spacing={1} alignItems="center" sx={{ mb: 3 }}>
            <AutoAwesomeIcon color="primary" />
            <Typography variant="h5" align="center">What should your flow do?</Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              Describe it the way you would to a colleague. You can change anything afterwards.
            </Typography>
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {busy ? <GenerationProgress startedAt={startedAt} progressNote={progressNote} /> : composer(true)}

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
            {EXAMPLES.map((example) => (
              <Chip
                key={example}
                label={example}
                variant="outlined"
                onClick={() => submit(example)}
                disabled={busy}
              />
            ))}
          </Stack>
        </Box>
      </Box>
    );
  }

  // The overflow button and its menu, shared by the desktop toolbar and the phone's compact one.
  const moreControls = (
    <>
      <Tooltip title="More actions: publish, send a test to WhatsApp, preview, auto-send triggers…">
        <IconButton
          size="small"
          onClick={(event) => setMoreAnchorEl(event.currentTarget)}
          data-testid="assistant-more-menu"
          aria-label="More actions"
          sx={{ border: '1px solid rgba(0, 0, 0, 0.12)', borderRadius: 1 }}
        >
          <MoreIcon />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={moreAnchorEl} open={!!moreAnchorEl} onClose={closeMoreMenu}>
        {narrow && (
          <MenuItem
            onClick={() => { closeMoreMenu(); setHistoryOpen(true); }}
            disabled={earlier.length === 0}
            data-testid="assistant-history-menu"
          >
            <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Earlier conversations</ListItemText>
          </MenuItem>
        )}
        <Tooltip title={publishDisabledReason || ''} placement="left">
          <span>
            <MenuItem
              onClick={() => { closeMoreMenu(); onPublish(); }}
              disabled={!!publishDisabledReason}
              data-testid="assistant-publish"
            >
              <ListItemIcon><PublishIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Publish</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip title={previewDisabledReason || ''} placement="left">
          <span>
            <MenuItem
              onClick={() => { closeMoreMenu(); onOpenPreview?.(); }}
              disabled={!onOpenPreview || !!previewDisabledReason}
              data-testid="assistant-preview"
            >
              <ListItemIcon><PhoneIphoneIcon fontSize="small" /></ListItemIcon>
              <ListItemText>WhatsApp Preview</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip title={triggersDisabledReason || ''} placement="left">
          <span>
            <MenuItem
              onClick={() => { closeMoreMenu(); onOpenTriggers?.(); }}
              disabled={!onOpenTriggers || !!triggersDisabledReason}
              data-testid="assistant-connections"
            >
              <ListItemIcon><HubIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Connections</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>
        {onStartAfresh && (
          <MenuItem
            onClick={() => { closeMoreMenu(); onStartAfresh(); }}
            disabled={busy}
            data-testid="start-afresh"
          >
            <ListItemIcon><RestartAltIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Start fresh</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );

  // The button says what pressing it would do. With nothing unsaved it reads "Saved" and rests:
  // pressing "Update Flow" on a saved flow re-synced it to WhatsApp and changed nothing on
  // screen, which a demo user on a phone reported (2026-10-09) as the button doing nothing.
  // A failed sync or auto-save keeps it live, since saving again is how to retry.
  const upToDate = !!flow?.id && !dirty && !autoSaveError && syncStatus?.syncStatus !== 'FAILED';
  const saveNow = async () => {
    setSaving(true);
    try {
      await onSaveNow();
    } finally {
      setSaving(false);
    }
  };
  const saveButton = (
    <Tooltip title={saveDisabledReason || ''}>
      <span>
        <Button
          variant="contained"
          size="small"
          disableElevation
          onClick={saveNow}
          disabled={!!saveDisabledReason || saving || upToDate}
          startIcon={saving
            ? <CircularProgress size={14} color="inherit" />
            : upToDate ? <CheckIcon fontSize="small" /> : null}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {saving ? 'Saving…' : upToDate ? 'Saved' : flow?.id ? 'Update Flow' : 'Save Flow'}
        </Button>
      </span>
    </Tooltip>
  );

  const saveStatus = (
    <SaveStatusControl
      dirty={dirty}
      autoSaveError={autoSaveError}
      syncStatus={syncStatus}
      autoSaveEnabled={autoSaveEnabled}
      onToggleAutoSave={onToggleAutoSave}
      onSaveNow={onSaveNow}
      canSave={!saveDisabledReason}
    />
  );

  const validationStatus = (
    <ValidationStatus
      validationMap={validationMap}
      screens={validationScreens}
      meta={validationMeta}
      onOpen={onOpenValidation}
      onFix={onFixProblems}
      busy={busy}
    />
  );

  /**
   * The toolbar on a phone: two short rows instead of four wrapped ones. The name, the one button
   * people press and the overflow menu first; the flow's state (type, problems, sync) in a strip
   * under it that scrolls sideways rather than wrapping. History moves into the menu. Reported
   * 2026-10-09 from a phone: the wrapped desktop toolbar took a third of the screen, with stray
   * dividers on lines of their own.
   */
  const compactHeader = (
    <Paper elevation={1} data-testid="assistant-header" sx={{ px: 1.5, pt: 1, pb: 0.75, borderRadius: 0, zIndex: 10 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <Typography variant="subtitle1" component="h1" noWrap sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
          {flow?.name || screens[0]?.title || 'New flow'}
        </Typography>
        {saveButton}
        {moreControls}
      </Box>
      <Box
        data-testid="assistant-status-strip"
        // The fade on the right says "there is more this way" when the strip is wider than the
        // phone; a hard cut through "Unsaved changes" read as broken rather than scrollable.
        sx={{
          display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, pr: 3, overflowX: 'auto',
          '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none',
          maskImage: 'linear-gradient(to right, #000 calc(100% - 28px), transparent)',
          WebkitMaskImage: 'linear-gradient(to right, #000 calc(100% - 28px), transparent)',
        }}
      >
        {onFlowTypeChange && <FlowTypeControl value={flowType} onChange={onFlowTypeChange} disabled={busy} />}
        {validationStatus}
        {saveStatus}
      </Box>
    </Paper>
  );

  const showChat = !narrow || mobileTab === 'chat';
  const showPreview = !narrow || mobileTab === 'preview';

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/*
        The manual builder's own toolbar, on the assistant side: same Paper, same title, same
        grouping, same overflow menu. Two modes of one product looked like two products,
        different heights, different type, actions in different corners, and toggling between
        them felt like leaving.

        What is NOT here is as deliberate as what is. Undo, redo and the component palette belong
        to a canvas this mode does not have. Import JSON is the opposite of why somebody is in
        the assistant. (Flow type IS here since 2026-09-28: the same control as the canvas, which
        the assistant sets from what it builds and says so.) And there is no "Manual mode" button any more: the tabs above already do that,
        and two switches for one thing is one too many.
      */}
      {hasFlow && narrow && compactHeader}
      {hasFlow && !narrow && (
        <Paper className="flow-builder__header" elevation={1} data-testid="assistant-header">
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, minWidth: 0, mr: 2 }}>
            <Typography variant="subtitle1" component="h1" noWrap sx={{ fontWeight: 600 }}>
              {flow?.name || screens[0]?.title || 'New flow'}
            </Typography>
          </Box>
          <Box className="flow-builder__actions">
            <Tooltip title={flow?.id ? 'Earlier conversations' : 'Save the flow to keep its history'}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => setHistoryOpen(true)}
                  disabled={earlier.length === 0}
                  aria-label="Earlier conversations"
                  data-testid="assistant-history"
                >
                  <HistoryIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />

            {onFlowTypeChange && (
              <>
                <FlowTypeControl value={flowType} onChange={onFlowTypeChange} disabled={busy} />
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />
              </>
            )}

            {/* The same validation control the canvas shows, opening the same panel; here "Fix
                these" hands the problems to the assistant. */}
            {validationStatus}

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {saveButton}
              {saveStatus}
            </Box>

            {moreControls}
          </Box>
        </Paper>
      )}
      {narrow && hasFlow && (
        <Tabs
          value={mobileTab}
          onChange={(event, next) => setMobileTab(next)}
          variant="fullWidth"
          sx={{ borderBottom: '1px solid', borderColor: 'divider', minHeight: 48 }}
        >
          <Tab value="chat" label="Chat" sx={{ minHeight: 48 }} />
          <Tab
            value="preview"
            label={screens.length > 1 ? `Preview (${screens.length})` : 'Preview'}
            sx={{ minHeight: 48 }}
          />
        </Tabs>
      )}

      <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
      {/* conversation */}
      <Box sx={{ flex: 1, minWidth: 0, display: showChat ? 'flex' : 'none', flexDirection: 'column' }}>
        <Box ref={scroller} sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {/* Capped and centred: on a wide screen an uncapped column runs to 900px, and a line
              that long is genuinely harder to read than a short one. */}
          <Stack spacing={1.5} sx={{ maxWidth: 720, mx: 'auto', width: '100%' }}>
            {/* Earlier conversations about this flow, from the server. Read-only until continued,
                so an old thread is never extended by accident. */}
            {earlier.length > 0 && !viewing && (
              <Box data-testid="conversation-history">
                <Button size="small" onClick={() => setHistoryOpen((open) => !open)} startIcon={<HistoryIcon fontSize="small" />}>
                  {`Earlier conversations (${earlier.length})`}
                </Button>
                {historyOpen && (
                  <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                    {earlier.map((session) => (
                      <Paper key={session.sessionId} variant="outlined" sx={{ px: 1.5, py: 1, cursor: 'pointer' }} onClick={() => onOpenSession(session.sessionId)} role="button" aria-label={`Open conversation: ${session.title}`}>
                        <Typography variant="body2" noWrap>{session.title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {`${relativeTime(session.lastTurnAt)} · ${session.turns} ${session.turns === 1 ? 'turn' : 'turns'}`}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Box>
            )}
            {viewing && (
              <Alert severity="info" data-testid="viewing-session"
                action={<Stack direction="row" spacing={1}>
                  <Button color="inherit" size="small" onClick={onCloseSession}>Back</Button>
                  <Button color="inherit" size="small" variant="outlined" onClick={onContinueSession}>Continue this conversation</Button>
                </Stack>}>
                {`An earlier conversation, ${relativeTime(viewing.startedAt)}`}
              </Alert>
            )}
            {(viewing ? viewing.messages : messages).map((message, index) => (
              <Box
                key={index}
                sx={{
                  alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '85%',
                }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 1.5,
                    py: 1,
                    bgcolor: message.role === 'user' ? 'primary.main' : 'background.paper',
                    color: message.role === 'user' ? 'primary.contrastText' : 'text.primary',
                    borderColor: message.role === 'user' ? 'primary.main' : 'divider',
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {message.text}
                  </Typography>
                </Paper>
                {message.summary && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {message.summary}
                  </Typography>
                )}
              </Box>
            ))}
            {busy && <GenerationProgress startedAt={startedAt} progressNote={progressNote} />}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </Box>
        {!viewing && (
          <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ maxWidth: 720, mx: 'auto' }}>{composer(false)}</Box>
          </Box>
        )}
      </Box>

      {/* live preview */}
      <Box
        sx={{
          // The flow is what they are here to look at, and 320px of it beside an unbounded
          // conversation had the proportions backwards. A share of the width rather than a
          // fixed strip, so it grows with the window instead of stranding the phone.
          width: narrow ? '100%' : 'clamp(360px, 38%, 560px)',
          flexShrink: 0,
          borderLeft: narrow ? 0 : '1px solid',
          borderColor: 'divider',
          display: showPreview ? 'flex' : 'none',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          p: narrow ? 1.5 : 2,
          overflowY: 'auto',
        }}
      >
        {hasFlow ? (
          <>
            <FlowPhonePreview
              flowJson={flowJson}
              width={narrow ? 240 : 288}
              screenIndex={screenIndex}
              variant="full"
            />
            {screens.length > 1 && (
              // On a phone the screen switcher goes above the preview, where it can be seen
              // without scrolling past the whole phone first.
              <Stack direction="row" spacing={1} alignItems="center" sx={{ order: narrow ? -1 : 0 }}>
                <IconButton
                  size="small"
                  aria-label="Previous screen"
                  disabled={screenIndex === 0}
                  onClick={() => setScreenIndex((i) => i - 1)}
                >
                  <ArrowBackIosNewIcon fontSize="inherit" />
                </IconButton>
                <Typography variant="caption" color="text.secondary">
                  Screen {screenIndex + 1} of {screens.length}
                </Typography>
                <IconButton
                  size="small"
                  aria-label="Next screen"
                  disabled={screenIndex >= screens.length - 1}
                  onClick={() => setScreenIndex((i) => i + 1)}
                >
                  <ArrowForwardIosIcon fontSize="inherit" />
                </IconButton>
              </Stack>
            )}
            {/* The one thing worth doing next: see the form on your own phone, fill it in, and
                watch the answers arrive. Offered above save and publish because it is what makes
                the rest of it mean anything. */}
            {onSendToPhone && (
              <Tooltip title={sendToPhoneDisabledReason || ''}>
                <span style={{ width: '100%' }}>
                  <Button
                    fullWidth
                    variant="contained"
                    disableElevation
                    startIcon={<PhoneIphoneIcon fontSize="small" />}
                    onClick={onSendToPhone}
                    disabled={!!sendToPhoneDisabledReason}
                    sx={{ py: 1.2 }}
                  >
                    Try it on your phone
                  </Button>
                </span>
              </Tooltip>
            )}

            {/* Save, publish and send-test used to sit here as a second cluster of buttons,
                which is why the two modes never looked like one product. They live in the
                toolbar now, where the manual builder keeps them. What stays below the preview is
                the one thing worth doing next. */}
            {assets.length > 0 && (
              <Box sx={{ width: '100%', mt: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                  Your images
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {assets.map((asset) => (
                    <Box key={asset.id} sx={{ position: 'relative' }}>
                      <Box
                        component="img"
                        src={assetUrl ? assetUrl(asset.id) : undefined}
                        alt={asset.description || asset.filename}
                        title={asset.description || asset.filename}
                        sx={{
                          width: 56, height: 56, objectFit: 'cover', borderRadius: 1.5,
                          border: '1px solid', borderColor: 'divider', display: 'block',
                        }}
                      />
                      <IconButton
                        size="small"
                        aria-label={`Remove ${asset.description || asset.filename}`}
                        onClick={() => onRemoveAsset && onRemoveAsset(asset.id)}
                        sx={{
                          position: 'absolute', top: -8, right: -8, width: 22, height: 22,
                          bgcolor: 'background.paper', border: '1px solid',
                          borderColor: 'divider',
                          '&:hover': { bgcolor: 'background.paper' },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 4 }}>
            Your flow will appear here.
          </Typography>
        )}
      </Box>
      </Box>
    </Box>
  );
};

export default AiFlowBuilder;
